import { TextProps, parseContent } from '@features/powerpoint';
import { theme } from '@features/executive-report/domain/theme';
import { dateFormatters, formatters } from '@utils/formattersV2';
import { headerTabs } from '../header-tabs';
import { RslReport } from '@features/campaigns/subdomains/rsl';
import { Campaign } from '@features/campaigns';

export const DIMENSION_TEXT_OFFSET = 2.99;

export type CampaignOverviewTextsOptions = {
  campaign: Pick<Campaign, 'adSpend' | 'startDate' | 'endDate'>;
  report: Pick<RslReport, 'reportDate' | 'impressions' | 'uniqueUsers'>;
  dimensionCounts: [number, number, number];
};

const fieldLabel = (
  text: string,
  options?: TextProps['options']
): TextProps => ({
  text: text.toUpperCase(),
  options: {
    fontSize: 9,
    color: theme.colors.white,
    h: 0.25,
    w: 1.27,
    x: 1.1,
    y: 3.52,
    ...options,
  },
});

const fieldValue = (
  text: TextProps['text'],
  options?: TextProps['options']
): TextProps => ({
  text,
  options: {
    fontSize: 16,
    color: theme.colors.white,
    bold: true,
    h: 0.37,
    w: 1.5,
    x: 1.1,
    y: 3.71,
    ...options,
  },
});

const dateRange = (startDate: string, endDate: string): TextProps['text'] => [
  {
    text: dateFormatters.shortLocaleString(new Date(startDate)),
  },
  {
    text: '  »  ',
    options: {
      color: theme.colors.inmarketGreen,
    },
  },
  {
    text: dateFormatters.shortLocaleString(new Date(endDate)),
  },
];

type DimensionTextsOptions = {
  title: string;
  count: number;
  name: string;
};

const dimensionTextOptions: readonly Omit<DimensionTextsOptions, 'count'>[] = [
  {
    title: 'Who',
    name: 'Audiences',
  },
  {
    title: 'What',
    name: 'Creatives',
  },
  {
    title: 'Where',
    name: 'Channels',
  },
] as const;

const dimensionTexts = (
  index: number,
  options: DimensionTextsOptions
): TextProps[] => [
  {
    text: parseContent(`The <lci>${options.title.toUpperCase()}</lci>`, 0, {
      color: theme.colors.white,
    }),
    options: {
      fontSize: 28,
      color: theme.colors.white,
      align: 'center',
      h: 0.53,
      w: 2.6,
      x: 4.36 + index * DIMENSION_TEXT_OFFSET,
      y: 2.63,
    },
  },
  {
    text: options.count.toString(),
    options: {
      fontSize: 40,
      color: theme.colors.inmarketGreen,
      align: 'center',
      h: 0.77,
      w: 0.89,
      x: 5.19 + index * DIMENSION_TEXT_OFFSET,
      y: 3.81,
    },
  },
  {
    text: options.name.toUpperCase(),
    options: {
      fontSize: 18,
      color: theme.colors.white,
      align: 'center',
      bold: true,
      h: 0.37,
      w: 2.71,
      x: 4.31 + index * DIMENSION_TEXT_OFFSET,
      y: 4.53,
    },
  },
];

export const texts = (options: CampaignOverviewTextsOptions): TextProps[] => [
  ...headerTabs('overview'),
  {
    text: parseContent('<b>Campaign</b> <lci>Overview</lci>', 0, {
      color: theme.colors.white,
    }),
    options: {
      fontSize: 28,
      h: 0.57,
      w: 11.83,
      x: 0.74,
      y: 0.74,
    },
  },
  fieldLabel('Ad Spend'),
  fieldValue(
    formatters.currency(options.campaign.adSpend ?? 0, {
      full: true,
      decimals: 0,
    })
  ),
  fieldLabel('Flight Dates', { x: 1.09, y: 4.14, w: 2.53 }),
  fieldValue(dateRange(options.campaign.startDate, options.campaign.endDate), {
    x: 1.09,
    y: 4.33,
    w: 3.43,
  }),
  fieldLabel('Measurement Period', { x: 1.09, y: 4.76, w: 2.53 }),
  fieldValue(dateRange(options.campaign.startDate, options.report.reportDate), {
    x: 1.09,
    y: 4.95,
    w: 3.43,
  }),
  fieldLabel('Unique Users Reached', { x: 1.09, y: 5.39, w: 2.53 }),
  fieldValue(
    formatters.users(options.report.uniqueUsers ?? 0, {
      full: true,
    }),
    { x: 1.09, y: 5.58, w: 3.15 }
  ),
  fieldLabel('Total Impressions', { x: 1.09, y: 5.97, w: 2.53 }),
  fieldValue(
    formatters.impressions(options.report.impressions, {
      full: true,
    }),
    { x: 1.09, y: 6.16, w: 3.15 }
  ),
  ...dimensionTextOptions.flatMap((option, index) =>
    dimensionTexts(index, {
      ...option,
      count: options.dimensionCounts[index],
    })
  ),
];
